// The packet / KPI-section rebounding page's pure logic (RD4, 2026-10-09): the percentile fills, the sample floors, the
// 5-game rule for the Last 5 / Season columns and the one dev-only toggle.
import {
  stepFromPercentile,
  stepHexOverWhite,
  WHITE_CELL_OUTLINE,
} from "../../../util/reportColors";

export interface CellFill {
  background: string;
  text: string;
  /** The light ring the reports put round a white middle-bucket cell. */
  outline?: string;
}

/** A cell's fill from its positional percentile (Trevor, 2026-10-10): the report scale's seven buckets, flattened onto
 * white as they print. One ink on every fill: the darkest is full red, where #111 still holds about 4.5:1. */
export function pctlFill(pctl: number): CellFill {
  const step = stepFromPercentile(pctl);
  return {
    background: stepHexOverWhite(step),
    text: "#111",
    ...(step === 0 ? { outline: WHITE_CELL_OUTLINE } : {}),
  };
}

/** The ownership page greys a cell (value kept, no percentile) under this many opportunities; the board never greys. */
export const MIN_OPPS = { game: 4, last5: 10, season: 25 };
export type Column = keyof typeof MIN_OPPS;

export const SAMPLE_GREY = { background: "#e4e2df", text: "#55524e" };

export const belowFloor = (opps: number, col: Column) => opps < MIN_OPPS[col];

/** Last 5 and Season appear once POR has played this many games of the season (Trevor, 2026-10-09). */
export const WINDOW_MIN_GAMES = 5;
export const showWindows = (porGamesToDate: number | null | undefined) =>
  (porGamesToDate ?? 0) >= WINDOW_MIN_GAMES;

// ------------------------------------------------------------------------------------------ dev-only toggle

export const VARIANTS_ENABLED = process.env.NODE_ENV === "development";

export interface Variants {
  /** Show Last 5 / Season before the 5th game, to preview the full layout. */
  forceWindows: boolean;
}

export const DEFAULT_VARIANTS: Variants = { forceWindows: false };

/** `?windows=1`, development only. */
export function readVariants(search: string, dev = VARIANTS_ENABLED): Variants {
  if (!dev) return DEFAULT_VARIANTS;
  return { forceWindows: new URLSearchParams(search).get("windows") === "1" };
}

/** The page's vertical fit (Trevor, 2026-10-09): player rows fill the sheet; the team Game boxes sit a little skinnier,
 * TEAM_ROW_SHARE of the player row with ROW_MIN as their floor. The tallest player row whose rows and team box fit
 * `room` (what is left once the fixed furniture is placed), within ROW_MIN..ROW_MAX. */
export const ROW_MIN = 26;
export const ROW_MAX = 40;
export const TEAM_ROW_SHARE = 0.85;
export const teamRowHeight = (row: number) => Math.max(ROW_MIN, Math.round(row * TEAM_ROW_SHARE));
export function fitRows(room: number, players: number, team: boolean) {
  for (let row = ROW_MAX; row > ROW_MIN; row--) {
    if (players * row + (team ? teamRowHeight(row) : 0) <= room) return row;
  }
  return ROW_MIN;
}
